/** Shared light-theme CSS tokens for public booking (no background paint). */
export const PUBLIC_BOOKING_LIGHT_TOKENS_CLASS = "public-booking-light-tokens";

/** Opaque floating panel chrome — portals must use this, never the canvas scope. */
export const PUBLIC_BOOKING_FLOATING_PANEL_CLASS =
  "public-booking-light-tokens z-[120] flex flex-col overflow-hidden rounded-xl border border-border/70 bg-white text-foreground shadow-lg";

export type PublicBookingFloatingPanelPos = {
  readonly left: number;
  readonly width: number;
  readonly openUp: boolean;
  readonly top: number;
  readonly bottom: number;
  readonly maxHeight: number;
};

const PANEL_GAP_PX = 6;
const PANEL_VIEWPORT_MARGIN_PX = 8;
const DEFAULT_DESIRED_HEIGHT_PX = 320;
const DEFAULT_MIN_WIDTH_PX = 288;
const FLIP_THRESHOLD_PX = 240;
const MIN_PANEL_HEIGHT_PX = 180;

/** Position a fixed panel anchored to a field — flips up when bottom space is tight. */
export function computePublicBookingFloatingPanelPos(
  anchor: HTMLElement,
  desiredHeightPx: number = DEFAULT_DESIRED_HEIGHT_PX,
  minWidthPx: number = DEFAULT_MIN_WIDTH_PX,
): PublicBookingFloatingPanelPos {
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const spaceBelow =
    viewportHeight - rect.bottom - PANEL_GAP_PX - PANEL_VIEWPORT_MARGIN_PX;
  const spaceAbove = rect.top - PANEL_GAP_PX - PANEL_VIEWPORT_MARGIN_PX;
  const openUp = spaceBelow < FLIP_THRESHOLD_PX && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    MIN_PANEL_HEIGHT_PX,
    Math.min(desiredHeightPx, openUp ? spaceAbove : spaceBelow),
  );
  const width = Math.max(rect.width, minWidthPx);
  const left = Math.max(
    PANEL_VIEWPORT_MARGIN_PX,
    Math.min(rect.left, viewportWidth - width - PANEL_VIEWPORT_MARGIN_PX),
  );

  return {
    left,
    width,
    openUp,
    top: rect.bottom + PANEL_GAP_PX,
    bottom: viewportHeight - rect.top + PANEL_GAP_PX,
    maxHeight,
  };
}
